import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Landmark, Search, ShieldCheck, ShieldAlert, Loader2, FileUp, Link2 } from "lucide-react";
import { api, errMsg, fdate, sha256File } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { StatusBadge, LegalPanel, Row, Hash, DemoBadge, SimBadge } from "@/components/common";

export default function Verify() {
  const { folio: param } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [folio, setFolio] = useState(param || "");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [hashRes, setHashRes] = useState(null);
  const [hashing, setHashing] = useState(false);
  const [hashInput, setHashInput] = useState("");

  const run = async (f) => {
    if (!f) return;
    setLoading(true); setErr(""); setRes(null);
    try {
      const { data } = await api.get(`/public/verify/${encodeURIComponent(f.trim().toUpperCase())}`);
      setRes(data);
    } catch (e) { setErr(errMsg(e, "Folio no encontrado")); } finally { setLoading(false); }
  };
  useEffect(() => { if (param) run(param); }, [param]); // eslint-disable-line

  const verifyHash = async (h) => {
    setHashing(true); setHashRes(null);
    try { const { data } = await api.post("/public/verify-hash", { hash: h }); setHashRes({ ...data, hash: h }); }
    catch (e) { setHashRes({ found: false, error: errMsg(e) }); } finally { setHashing(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <button className="flex items-center gap-2.5" onClick={() => navigate(user ? "/app" : "/")} data-testid="verify-home-link">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white"><Landmark className="h-5 w-5" /></span>
            <span className="font-display font-semibold text-lg">HitchGob</span>
          </button>
          <span className="text-sm text-muted-foreground">Verificación pública</span>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-semibold tracking-tight">Verificar autenticidad</h1>
        <p className="text-muted-foreground mt-1">Consulta cualquier folio (reporte, trámite, multa o pago) o comprueba la huella de un documento.</p>
        <Tabs defaultValue="folio" className="mt-6">
          <TabsList><TabsTrigger value="folio" data-testid="verify-tab-folio">Por folio</TabsTrigger><TabsTrigger value="hash" data-testid="verify-tab-hash">Por documento / hash</TabsTrigger></TabsList>
          <TabsContent value="folio">
            <form className="flex gap-2 mt-3" onSubmit={(e) => { e.preventDefault(); const f = folio.trim().toUpperCase(); if (f === param) run(f); else navigate(`/verificar/${f}`); }}>
              <Input value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="QR-MUL-2026-000001" className="font-mono bg-white" data-testid="verify-folio-input" />
              <Button type="submit" data-testid="verify-folio-submit-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}<span className="ml-1">Verificar</span></Button>
            </form>
            {err && <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-800 text-sm" data-testid="verify-error">{err}</div>}
            {res && (
              <div className="mt-6 grid md:grid-cols-[1fr_260px] gap-4 fade-up" data-testid="verify-result">
                <Card className="bg-white">
                  <CardHeader className="pb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {res.chain_valid && (res.signature_valid !== false)
                        ? <span className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-medium" data-testid="verify-valid"><ShieldCheck className="h-5 w-5" /> Registro auténtico e íntegro</span>
                        : <span className="inline-flex items-center gap-1.5 text-rose-700 text-sm font-medium" data-testid="verify-invalid"><ShieldAlert className="h-5 w-5" /> No se pudo validar</span>}
                      {res.is_demo && <DemoBadge />}
                    </div>
                    <CardTitle className="text-xl">{res.title}</CardTitle>
                    <div className="flex items-center gap-2"><span className="font-mono text-sm">{res.folio}</span><StatusBadge status={res.status} /></div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm">
                      {Object.entries(res.details || {}).map(([k, v]) => <Row key={k} label={k}><span className="text-sm">{v}</span></Row>)}
                      <Row label="Emitido"><span className="text-sm">{fdate(res.issued_at)}</span></Row>
                    </div>
                    <LegalPanel data_hash={res.data_hash} nom151={res.nom151} anchor={res.anchor} signature={res.signature}
                      signatureValid={res.signature ? res.signature_valid : undefined} />
                    {res.trail?.length > 0 && (
                      <div>
                        <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><Link2 className="h-4 w-4" /> Historial anclado ({res.trail.length})</div>
                        <div className="space-y-1.5">
                          {res.trail.map((t, i) => (
                            <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-secondary px-3 py-2 text-xs">
                              <span className="font-medium">{t.event}</span><span className="text-muted-foreground">{fdate(t.timestamp)}</span>
                              <Hash value={t.tx_hash} n={8} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {res.payable && (
                      <Button className="w-full btn-press" data-testid="verify-pay-fine-button"
                        onClick={() => (user ? navigate(`/app/pagos?multa=${res.folio}`) : login())}>
                        Pagar en línea (50% pronto pago dentro de 10 días)
                      </Button>
                    )}
                  </CardContent>
                </Card>
                <Card className="bg-white h-fit">
                  <CardContent className="p-5 flex flex-col items-center gap-3">
                    <QRCodeSVG value={`${window.location.origin}/verificar/${res.folio}`} size={180} fgColor="#16202B" />
                    <div className="text-xs text-muted-foreground text-center">Escanea para verificar este folio</div>
                    <div className="text-xs text-center">Cadena de auditoría: {res.chain_valid ? <b className="text-emerald-700">íntegra</b> : <b className="text-rose-700">alterada</b>}</div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
          <TabsContent value="hash">
            <Card className="mt-3 bg-white"><CardContent className="p-5 space-y-4">
              <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer hover:bg-secondary transition-colors">
                <FileUp className="h-6 w-6 text-primary" />
                <span className="text-sm">Selecciona un documento: calculamos su SHA-256 en tu navegador (no se sube)</span>
                <input type="file" className="hidden" data-testid="verify-file-input" onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return; const h = await sha256File(f); setHashInput(h); verifyHash(h);
                }} />
              </label>
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); verifyHash(hashInput); }}>
                <Input value={hashInput} onChange={(e) => setHashInput(e.target.value)} placeholder="Hash SHA-256 (64 caracteres)" className="font-mono text-xs" data-testid="verify-hash-input" />
                <Button type="submit" data-testid="verify-hash-submit-button">{hashing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Comprobar"}</Button>
              </form>
              {hashRes && (hashRes.found
                ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="verify-hash-found">
                    Huella registrada en <b className="font-mono">{hashRes.folio}</b> ({hashRes.kind}{hashRes.event ? ` · ${hashRes.event}` : ""}).
                    <Button variant="link" className="px-1" onClick={() => { setFolio(hashRes.folio); navigate(`/verificar/${hashRes.folio}`); }}>Ver folio</Button>
                  </div>
                : <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="verify-hash-not-found">Esta huella no está registrada en HitchGob.</div>)}
              <div className="text-xs text-muted-foreground flex items-center gap-2">Sello NOM-151 y anclaje en Polygon <SimBadge /></div>
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
